import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  startMembershipCheckout,
  type MembershipInterval,
  type Program,
  type WaiverKind,
} from '@/lib/api';
import { events, track } from '@/lib/analytics';
import { CheckoutStepper } from './Stepper';
import { ProgramsStep, tierFromPrograms } from './ProgramsStep';
import { LoginStep, type AuthSession } from './LoginStep';
import { loadAuthSession } from './authSessionStorage';
import { type SignedSet, type WaiverPersonalInfo } from './WaiversStep';
import { type IdentityStatus } from './IdentityStep';
import { WaiverAndIdentityStep, type WaiverAndIdentityFieldErrors } from './WaiverAndIdentityStep';
import { ConfirmStep } from './ConfirmStep';

const EMPTY_PERSONAL_INFO: WaiverPersonalInfo = {
  address: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  dateOfBirth: '',
  medicalConditions: '',
};

const PROGRAM_LABEL: Record<Program, string> = {
  Boxing: 'Boxing',
  MuayThai: 'Muay Thai',
  MMA: 'MMA',
  BJJ: 'Brazilian Jiu-Jitsu',
  Kids: 'Kids',
};

const REQUIRED_MEMBERSHIP_WAIVERS: WaiverKind[] = [
  'LIABILITY_ADULT',
  'SPARRING_ADULT',
  'MEMBERSHIP_TERMS',
  'BILLING_TERMS',
];

const MEMBERSHIP_STEP_VIEW_EVENTS = [
  events.membershipSignupStep1,
  events.membershipSignupStep2,
  events.membershipSignupStep3,
  events.membershipSignupStep4,
] as const;

export function requiredMembershipWaivers(
  programs: Program[],
  minorParticipant: boolean
): WaiverKind[] {
  if (programs.includes('Kids')) {
    return ['LIABILITY_MINOR', 'MEMBERSHIP_TERMS', 'BILLING_TERMS'];
  }
  if (minorParticipant) {
    return ['LIABILITY_MINOR', 'SPARRING_MINOR', 'MEMBERSHIP_TERMS', 'BILLING_TERMS'];
  }
  return REQUIRED_MEMBERSHIP_WAIVERS;
}

interface JoinFieldErrors {
  programs?: string | null;
  phone?: string | null;
  waivers: WaiverAndIdentityFieldErrors;
}

const EMPTY_FIELD_ERRORS: JoinFieldErrors = { waivers: {} };

export function JoinForm({
  gateAthletes,
  checkoutCode,
}: {
  gateAthletes: boolean;
  checkoutCode?: string;
}) {
  const [initialSession] = useState<AuthSession | null>(() => loadAuthSession());
  const [params] = useSearchParams();
  const discountCode = checkoutCode ?? params.get('code') ?? undefined;

  const [programs, setPrograms] = useState<Program[]>([]);
  const [minorParticipant, setMinorParticipant] = useState(false);
  const [interval, setInterval] = useState<MembershipInterval>('MONTHLY');
  // The QR-code URL is the source of truth for athlete pricing — there is no
  // user-toggleable checkbox, and athletes are always MONTHLY (no yearly SKU).
  const athletes = gateAthletes;
  useEffect(() => {
    if (gateAthletes && interval !== 'MONTHLY') setInterval('MONTHLY');
  }, [gateAthletes, interval]);

  const [email, setEmail] = useState(initialSession?.email ?? '');
  const [session, setSession] = useState<AuthSession | null>(initialSession);

  const [legalName, setLegalName] = useState('');
  const [signed, setSigned] = useState<SignedSet>({});
  const [personalInfo, setPersonalInfo] = useState<WaiverPersonalInfo>(EMPTY_PERSONAL_INFO);
  const [identityStatus, setIdentityStatus] = useState<IdentityStatus>('unverified');
  const [phone, setPhone] = useState('');
  // Phone is collected once in step 3 (waiver) — propagate to step 4 (confirm)
  // so the Stripe checkout call has the canonical formatted value.
  useEffect(() => {
    if (personalInfo.phone && personalInfo.phone !== phone) setPhone(personalInfo.phone);
  }, [personalInfo.phone, phone]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [fieldErrors, setFieldErrors] = useState<JoinFieldErrors>(EMPTY_FIELD_ERRORS);
  const clearProgramsError = () => setFieldErrors((p) => ({ ...p, programs: null }));
  const clearPhoneError = () => setFieldErrors((p) => ({ ...p, phone: null }));
  const clearWaiverError = (key: keyof WaiverAndIdentityFieldErrors) =>
    setFieldErrors((p) => ({ ...p, waivers: { ...p.waivers, [key]: null } }));

  const tier = useMemo(() => tierFromPrograms(programs), [programs]);
  const requiredWaivers = useMemo(
    () => requiredMembershipWaivers(programs, minorParticipant),
    [programs, minorParticipant]
  );
  const allWaiversSigned = requiredWaivers.every((k) => Boolean(signed[k]));

  const trackMembershipStepView = useCallback(
    (_step: unknown, index: number) => {
      const event = MEMBERSHIP_STEP_VIEW_EVENTS[index];
      if (!event) return;
      track(event, {
        funnel: 'membership_signup',
        step: index + 1,
        athletes,
        tier,
        interval,
        programs,
      });
    },
    [athletes, interval, programs, tier]
  );

  async function submitCheckout() {
    if (!tier || !session) return;
    setSubmitting(true);
    setSubmitError(null);
    track(events.joinCheckoutSubmit, { tier, interval, athletes, programs });
    try {
      const { url } = await startMembershipCheckout({
        name: legalName,
        email: session.email,
        phone,
        tier,
        interval,
        athletes,
        discountCode,
        programs,
        authToken: session.authToken,
      });
      window.location.href = url;
    } catch (err) {
      const msg = String((err as Error).message ?? err);
      track(events.joinCheckoutError, { error: msg });
      setSubmitError(
        msg === 'price_unavailable'
          ? 'That plan is not currently available. Please contact us.'
          : msg === 'athlete_code_invalid'
            ? 'This athlete pricing link is invalid or expired. Please contact us.'
            : 'Something went wrong. Please try again.'
      );
      setSubmitting(false);
    }
  }

  return (
    <CheckoutStepper
      onComplete={() => void submitCheckout()}
      onStepView={trackMembershipStepView}
      steps={[
        {
          id: 'programs',
          title: '1. Pick your programs',
          body: () => (
            <ProgramsStep
              programs={programs}
              setPrograms={setPrograms}
              minorParticipant={minorParticipant}
              setMinorParticipant={setMinorParticipant}
              athletes={athletes}
              fieldError={fieldErrors.programs}
              clearFieldError={clearProgramsError}
            />
          ),
          summary: () =>
            tier
              ? `${programs.map((p) => PROGRAM_LABEL[p]).join(', ')}${
                  athletes ? ' · athlete pricing' : ''
                }${programs.includes('Kids') || minorParticipant ? ' · minor' : ''}`
              : null,
          validate: () => {
            if (programs.length === 0) {
              const message = 'Pick at least one program to continue.';
              setFieldErrors((p) => ({ ...p, programs: message }));
              return { message, focusId: 'field-programs' };
            }
            if (!tier) {
              const message = 'Kids program cannot be combined with adult disciplines.';
              setFieldErrors((p) => ({ ...p, programs: message }));
              return { message, focusId: 'field-programs' };
            }
            setFieldErrors((p) => ({ ...p, programs: null }));
            return null;
          },
        },
        {
          id: 'login',
          title: '2. Sign-In',
          hideContinue: true,
          body: ({ next }) => (
            <LoginStep
              email={email}
              setEmail={setEmail}
              session={session}
              setSession={setSession}
              next={next}
            />
          ),
          summary: () => (session ? `Signed in as ${session.email}` : null),
          validate: () => (session ? null : { message: 'Sign in to continue.' }),
        },
        {
          id: 'waivers',
          title: '3. Sign Waiver',
          body: () =>
            session ? (
              <WaiverAndIdentityStep
                required={requiredWaivers}
                authToken={session.authToken}
                legalName={legalName}
                setLegalName={setLegalName}
                signed={signed}
                setSigned={setSigned}
                signerEmail={session.email}
                personalInfo={personalInfo}
                setPersonalInfo={setPersonalInfo}
                identityStatus={identityStatus}
                setIdentityStatus={setIdentityStatus}
                fieldErrors={fieldErrors.waivers}
                clearFieldError={clearWaiverError}
              />
            ) : (
              <p className="text-sm text-bone/60">Sign in first.</p>
            ),
          summary: () => {
            if (allWaiversSigned && identityStatus === 'verified') {
              return `${requiredWaivers.length} waivers signed · ID verified`;
            }
            if (allWaiversSigned) return `${requiredWaivers.length} waivers signed`;
            return null;
          },
          validate: () => {
            const next: WaiverAndIdentityFieldErrors = {};
            let firstFocus: string | null = null;
            let topMessage: string | null = null;

            if (legalName.length < 2) {
              next.legalName = 'Type your legal full name to use as your signature.';
              if (!firstFocus) {
                firstFocus = 'field-legal-name';
                topMessage = next.legalName;
              }
            }
            const liabilityRequired = requiredWaivers.some(
              (k) => k === 'LIABILITY_ADULT' || k === 'LIABILITY_MINOR'
            );
            const addressOk =
              personalInfo.address.trim().length >= 3 &&
              personalInfo.city.trim().length >= 2 &&
              personalInfo.state.trim().length >= 2 &&
              /^\d{5}(-\d{4})?$/.test(personalInfo.zip.trim());
            const dateOfBirthOk = /^\d{4}-\d{2}-\d{2}$/.test(personalInfo.dateOfBirth.trim());
            if (liabilityRequired && !addressOk) {
              next.address =
                'Enter your address (street, city, state, zip) for the liability release.';
              if (!firstFocus) {
                firstFocus = 'field-address';
                topMessage = next.address;
              }
            }
            if (liabilityRequired && !dateOfBirthOk) {
              next.dateOfBirth = 'Enter your date of birth for the waiver forms.';
              if (!firstFocus) {
                firstFocus = 'field-date-of-birth';
                topMessage = next.dateOfBirth;
              }
            }
            const missing = requiredWaivers.filter((k) => !signed[k]);
            if (missing.length > 0) {
              next.waivers = `Please sign all required waivers (${missing.length} remaining).`;
              if (!firstFocus) {
                firstFocus = 'field-waivers';
                topMessage = next.waivers;
              }
            }
            if (identityStatus !== 'verified') {
              next.identity = 'Verify your government ID to continue.';
              if (!firstFocus && missing.length === 0 && legalName.length >= 2) {
                firstFocus = 'field-identity';
                topMessage = next.identity;
              }
            }

            setFieldErrors((p) => ({ ...p, waivers: next }));
            if (topMessage) {
              return { message: topMessage, focusId: firstFocus ?? undefined };
            }
            return null;
          },
        },
        {
          id: 'confirm',
          title: '4. Confirm & pay',
          body: () => (
            <ConfirmStep
              tier={tier}
              athletes={athletes}
              interval={interval}
              setInterval={setInterval}
              programs={programs}
              email={session?.email ?? ''}
              phone={phone}
              setPhone={setPhone}
              submitting={submitting}
              submitError={submitError}
              phoneError={fieldErrors.phone}
              clearPhoneError={clearPhoneError}
            />
          ),
          summary: () => null,
          validate: () => {
            if (phone.replace(/\D/g, '').length < 7) {
              const message = 'Enter your phone number.';
              setFieldErrors((p) => ({ ...p, phone: message }));
              return { message, focusId: 'field-phone' };
            }
            setFieldErrors((p) => ({ ...p, phone: null }));
            return null;
          },
        },
      ]}
    />
  );
}
